import Button from "atoms/buttons/button";
import { ButtonStyles } from "atoms/constants/button-styles";
import { t } from "i18next";
import UserPublicationFavoriteRecord from "models/view-models/user-publication-favorite-record";
import { SearchFormStyles } from "molecules/enums/search-form-style";
import SearchForm from "molecules/forms/search-form";
import PublicationSelectionModal from "organisms/modals/publication-selection-modal/publication-selection-modal";
import PublicationsList from "organisms/modals/publication-selection-modal/publications-list";
import React, {
    ChangeEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import usePublicationCodeGroups from "utilities/hooks/domain/publications/use-publication-code-groups";
import usePublicationFavorites from "utilities/hooks/domain/user-publication-favorites/use-publication-favorites";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import useOutsideClick from "utilities/hooks/use-outside-click";

const CSS_BASE_CLASS_NAME = "c-user-dashboard-publications-dropdown";
interface UserDashboardPublicationsDropdownProps {
    isOpen: boolean;
    handleOpen: () => void;
    handleClose: () => void;
}

const UserDashboardPublicationsDropdown = (
    props: UserDashboardPublicationsDropdownProps
) => {
    const { handleClose, handleOpen, isOpen } = props;
    const {
        loading: publicationGroupsLoading,
        publicationGroups,
        handleSearch,
        setSkip,
    } = usePublicationCodeGroups({});

    const searchRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const intersectionObserverRef = useRef<IntersectionObserver | null>(null);
    const latestPublicationData = useRef({
        publicationGroupsLoading,
        publicationGroupsCount: publicationGroups.length,
    });
    latestPublicationData.current = {
        publicationGroupsLoading,
        publicationGroupsCount: publicationGroups.length,
    };
    const [searchText, setSearchText] = useState("");

    const { isOnline } = useNetworkInformation();

    const modalTitle = t("allItem", {
        item: t("publication_plural"),
    });
    const handleSearchClear = () => {
        setSearchText("");
        handleSearch("");
    };
    const handleSearchTextChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.currentTarget.value);
    };
    const {
        favorites,
        setFavorites,
        refresh: refreshFavorites,
    } = usePublicationFavorites();

    const setAndRefreshFavorites = useCallback(
        (favorites: Array<UserPublicationFavoriteRecord>): void => {
            setFavorites(favorites);
            refreshFavorites();
        },
        [refreshFavorites, setFavorites]
    );

    useOutsideClick([modalRef, searchRef], () => {
        handleClose();
    });

    const handleSearchClick = () => {
        handleSearch(searchText);
        if (searchText && !isOpen) {
            handleOpen();
        }
    };

    const isMobile = useBreakpoint(
        Breakpoints.SmallTablet,
        BreakpointComparer.MaxWidth
    );

    const observerRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (node !== null && !publicationGroupsLoading) {
                if (intersectionObserverRef.current != null)
                    intersectionObserverRef.current.disconnect();

                intersectionObserverRef.current = new IntersectionObserver(
                    (entries: IntersectionObserverEntry[]) => {
                        const {
                            publicationGroupsLoading: loading,
                            publicationGroupsCount: count,
                        } = latestPublicationData.current;

                        if (loading || count === 0) return;

                        if (entries.some((e) => e.isIntersecting)) {
                            setSkip((prev) => prev + 1);
                        }
                    },
                    { threshold: 0.5 }
                );
                intersectionObserverRef.current.observe(node);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setSkip]
    );

    useEffect(() => {
        return () => intersectionObserverRef.current?.disconnect();
    }, []);

    return (
        <div className={`${CSS_BASE_CLASS_NAME}`}>
            <div
                className={`${CSS_BASE_CLASS_NAME}__search${isOnline ? "" : "__disabled"}`}
                ref={searchRef}>
                <SearchForm
                    dataTestId="user-dashboard--publications-dropdown--form"
                    disabled={!isOnline}
                    onClear={handleSearchClear}
                    onSearchClick={handleSearchClick}
                    onSearchTextChange={handleSearchTextChange}
                    onClick={handleOpen}
                    placeholder={
                        isOpen
                            ? t("publicationSelectionModal-searchPlaceholder")
                            : t("findAPublication")
                    }
                    searchText={searchText}
                    style={SearchFormStyles.TertiaryAlt}
                />
            </div>
            <>
                {isOpen && !isMobile && (
                    <div
                        aria-label={modalTitle}
                        className={`${CSS_BASE_CLASS_NAME}__modal`}>
                        <div>
                            <div className="c-modal__content" ref={modalRef}>
                                <div className={`${CSS_BASE_CLASS_NAME}__body`}>
                                    <PublicationsList
                                        favorites={favorites}
                                        onChangeFavorites={
                                            setAndRefreshFavorites
                                        }
                                        publicationGroups={publicationGroups}
                                        loading={publicationGroupsLoading}
                                        observerRef={observerRef}
                                    />
                                </div>
                                <div
                                    className={`${CSS_BASE_CLASS_NAME}__footer`}>
                                    <Button
                                        onClick={handleClose}
                                        style={ButtonStyles.Tertiary}>
                                        {t("close")}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </>
            <PublicationSelectionModal
                isVisible={isMobile && isOpen}
                favorites={favorites}
                onChangeFavorites={setAndRefreshFavorites}
                onResultClick={() => {}}
                closeDialog={handleClose}
            />
        </div>
    );
};

export default UserDashboardPublicationsDropdown;
